import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { clientSectorAPI, leadAPI, settingsAPI, userAPI, b2bAPI, assignmentAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { SectorSelect } from '../components/SectorSelect';
import { ArrowLeft, Save, CheckCircle2, Flame } from 'lucide-react';
import { CompassPicker, facingFromAngle } from '../components/CompassPicker';
import { LocationPicker, emptyLocation } from '../components/LocationPicker';

const PROJECT_TYPES = ['residential', 'commercial', 'industrial'];
const PLOT_SIZE_UNITS = [['feet', 'Feet'], ['meter', 'Meter']];
const PLOT_AREA_UNITS = [['sqft', 'Sq.ft'], ['sqm', 'Sq.m'], ['sqyd', 'Sq.yd'], ['gaj', 'Gaj'], ['bigha', 'Bigha'], ['acre', 'Acre']];
const CONSTRUCTION_UNITS = [['sqft', 'Sq.ft'], ['sqm', 'Sq.m'], ['sqyd', 'Sq.yd']];
const POSITIONS = [['middle', 'Middle'], ['corner', 'Corner'], ['three_side_open', 'Three Side Open'], ['four_side_open', 'Four Side Open']];
const FACINGS = [['north', 'North'], ['north_east', 'North-East'], ['east', 'East'], ['south_east', 'South-East'],
  ['south', 'South'], ['south_west', 'South-West'], ['west', 'West'], ['north_west', 'North-West'], ['custom', 'Custom / Compass']];
const PRIORITIES = [['hot', '\u{1F525} Hot'], ['warm', '\u{1F7E1} Warm'], ['cold', '\u{1F535} Cold']];
const LEGACY_PRIORITIES = [['high', 'High (legacy)'], ['medium', 'Medium (legacy)'], ['low', 'Low (legacy)'], ['urgent', 'Urgent (legacy)'], ['vip', 'VIP (legacy)']];
const STATUSES = [['new_lead', 'Active'], ['future_prospect', 'Future Prospect'], ['not_interested', 'Not Interested'], ['lost', 'Lost'], ['duplicate', 'Duplicate']];
const REASON_REQUIRED = ['not_interested', 'lost'];
// Server is the source of truth (GET /leads/form-config); these are only safe fallbacks.
const FALLBACK_FORM_CONFIG = {
  required_fields: ['primary_mobile', 'customer_name'], locked_required_fields: ['primary_mobile'],
  contact_fields: ['primary_mobile', 'alternative_number', 'whatsapp_number', 'email', 'client_mobile', 'client_whatsapp', 'client_email'],
  source_fields: ['lead_source', 'campaign_name'],
  can_edit: true, can_view_contact: true, can_edit_contact: true, can_edit_source: true,
};
const FIELD_LABELS = {
  customer_name: 'Customer / Contact Name', primary_mobile: 'Primary Mobile', alternative_number: 'Alternative Number',
  whatsapp_number: 'WhatsApp Number', email: 'Email', company_name: 'Company Name',
  project_address: 'Street / Site Address', city: 'City', project_type: 'Project Type', plot_size: 'Plot Size',
  construction_area: 'Construction Area', lead_source: 'Lead Source', campaign_name: 'Campaign Name',
  follow_up_date: 'Follow-up Date', budget: 'Estimated Budget', priority: 'Priority', status: 'Lead Status',
  remarks: 'Remarks',
};

const emptyForm = {
  client_type: 'b2c', partner_id: '', client_name: '', client_mobile: '', client_whatsapp: '', client_email: '', client_company_name: '',
  customer_name: '', primary_mobile: '', alternative_number: '', whatsapp_number: '', whatsapp_same_as_primary: false,
  email: '', company_name: '',
  project_address: '', city: '', state: '', pin_code: '', google_maps_location: '',
  ...emptyLocation,
  project_type: '', plot_size: '', plot_size_unit: 'feet', plot_area: '', plot_area_unit: 'sqft',
  construction_area: '', construction_area_unit: 'sqft', plot_position: '', plot_facing: '', plot_facing_angle: 0,
  client_sector: '', num_floors: '', expected_start_date: '', site_visit_date: '', follow_up_date: '', services_required: [],
  lead_source: 'Website', campaign_name: '', assigned_to: '', priority: 'warm', status: 'new_lead', status_reason: '',
  budget: '', probability: 0, tags: [], remarks: '',
  // legacy closure fields — only editable in Edit mode (see B5C report)
  project_name: '', final_deal_amount: '', advance_received: '', agreement_signed: false, agreement_date: '', project_status: ''
};

const toDateInput = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');
const lbl = 'block text-xs font-medium mb-1';
const fld = 'w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm focus:ring-2 focus:ring-primary/40 outline-none';

const Section = ({ title, hint, children, testId }) => (
  <section className="rounded-xl border border-slate-200 px-3.5 py-3 dark:border-slate-700" data-testid={testId}>
    <div className="mb-2 flex flex-wrap items-baseline gap-2">
      <h3 className="text-base font-medium">{title}</h3>
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
    </div>
    {children}
  </section>
);

export const LeadFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const isEdit = Boolean(id);
  const idem = useRef(`lf-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
  const [loading, setLoading] = useState(false);
  const [leadSources, setLeadSources] = useState([]);
  const [services, setServices] = useState([]);
  const [users, setUsers] = useState([]);
  const [partners, setPartners] = useState(null);
  const [intel, setIntel] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [created, setCreated] = useState(null);
  const [duplicateWarning, setDuplicateWarning] = useState([]);
  const [cfg, setCfg] = useState(FALLBACK_FORM_CONFIG);
  const [sectors, setSectors] = useState([]);

  const canAssign = hasPermission('leads.assign');
  // Add Lead never implies Edit Lead — editing an existing lead needs its own permission.
  const blockedEdit = isEdit && !hasPermission('leads.edit');
  const isRequired = (field) => (cfg.required_fields || []).includes(field);
  const star = (field) => (isRequired(field) ? ' *' : '');
  // Contact / Source are protected groups: read-only unless separately authorized (backend enforced too)
  const contactLocked = isEdit && !cfg.can_edit_contact;
  const sourceLocked = isEdit && !cfg.can_edit_source;
  const sectorLocked = isEdit && cfg.can_edit_sector === false;

  const fetchInitialData = useCallback(async () => {
    const [sourcesRes, servicesRes, usersRes] = await Promise.all([
      settingsAPI.getLeadSources().catch(() => ({ data: [] })),
      settingsAPI.getServices().catch(() => ({ data: [] })),
      userAPI.getAll().catch(() => ({ data: [] }))
    ]);
    setLeadSources(sourcesRes.data || []);
    setServices(servicesRes.data || []);
    setUsers(usersRes.data || []);
    leadAPI.formConfig().then((r) => setCfg({ ...FALLBACK_FORM_CONFIG, ...(r.data || {}) })).catch(() => {});
    clientSectorAPI.list().then((r) => setSectors(r.data || [])).catch(() => setSectors([]));
    b2bAPI.list().then((r) => setPartners(r.data?.partners || [])).catch(() => setPartners([]));
    assignmentAPI.intelligence().then((r) => setIntel(r.data)).catch(() => setIntel(null));
  }, []);

  const fetchLead = useCallback(async () => {
    try {
      const { data } = await leadAPI.getById(id);
      const clean = {};
      Object.entries(data).forEach(([k, v]) => { if (k in emptyForm) clean[k] = v === null || v === undefined ? emptyForm[k] : v; });
      setForm({
        ...emptyForm, ...clean,
        budget: data.budget ?? '', advance_received: data.advance_received ?? '', final_deal_amount: data.final_deal_amount ?? '',
        probability: data.probability ?? 0, plot_facing_angle: data.plot_facing_angle ?? 0,
        expected_start_date: toDateInput(data.expected_start_date), follow_up_date: toDateInput(data.follow_up_date),
        site_visit_date: toDateInput(data.site_visit_date), agreement_date: toDateInput(data.agreement_date),
        client_type: data.client_type || 'b2c', status: data.status || 'new_lead', status_reason: data.status_reason || '',
        plot_size_unit: data.plot_size_unit || 'feet', plot_area_unit: data.plot_area_unit || 'sqft',
        construction_area_unit: data.construction_area_unit || 'sqft',
        services_required: data.services_required || [], tags: data.tags || [], assigned_to: data.assigned_to || ''
      });
    } catch (e) { toast.error('Failed to load lead'); }
  }, [id]);

  useEffect(() => { fetchInitialData(); if (isEdit) fetchLead(); }, [isEdit, fetchInitialData, fetchLead]);

  const set = (key, value) => setForm((prev) => {
    const next = { ...prev, [key]: value };
    if (key === 'primary_mobile' && prev.whatsapp_same_as_primary) next.whatsapp_number = value;
    if (key === 'whatsapp_same_as_primary' && value) next.whatsapp_number = prev.primary_mobile;
    if (key === 'num_floors') next.num_floors = String(value).toUpperCase();
    return next;
  });

  const execMetrics = useMemo(() => {
    const map = {};
    (intel?.executives || []).forEach((e) => { map[e.user_id] = e; });
    return map;
  }, [intel]);
  const selectedExec = execMetrics[form.assigned_to];

  const toggleService = (name) => setForm((p) => ({
    ...p, services_required: p.services_required.includes(name)
      ? p.services_required.filter((s) => s !== name) : [...p.services_required, name]
  }));

  const validate = () => {
    if (form.primary_mobile.replace(/\D/g, '').length < 10) return 'A valid Primary Mobile is required';
    if (!isEdit) {
      const missing = (cfg.required_fields || []).find((f) => {
        if (f === 'primary_mobile') return false;
        const v = form[f];
        return v === '' || v == null || (Array.isArray(v) && v.length === 0);
      });
      if (missing) return `${FIELD_LABELS[missing] || missing} is required`;
    }
    if (form.client_type === 'b2b') {
      if (!form.partner_id) return 'Select the B2B Partner';
      if (!form.client_name.trim()) return 'Client / End Customer Name is required';
      if (form.client_mobile.replace(/\D/g, '').length < 10) return 'A valid Client / End Customer Mobile is required';
    }
    if (form.plot_area !== '' && Number.isNaN(Number(form.plot_area))) return 'Plot Area must be a number';
    if (REASON_REQUIRED.includes(form.status) && !form.status_reason.trim()) return `A reason is required for status "${STATUSES.find((s) => s[0] === form.status)[1]}"`;
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!isEdit) payload.idempotency_key = idem.current;
      ['budget', 'final_deal_amount', 'advance_received'].forEach((k) => { payload[k] = payload[k] !== '' && payload[k] != null ? parseFloat(payload[k]) : null; });
      payload.probability = payload.probability !== '' && payload.probability != null ? parseInt(payload.probability, 10) : null;
      payload.plot_facing_angle = payload.plot_facing === 'custom' ? Number(payload.plot_facing_angle) : null;
      ['expected_start_date', 'follow_up_date', 'site_visit_date', 'agreement_date'].forEach((k) => {
        payload[k] = payload[k] ? new Date(payload[k]).toISOString() : null;
      });
      if (!isEdit) ['project_name', 'final_deal_amount', 'advance_received', 'agreement_date', 'project_status'].forEach((k) => { payload[k] = null; });
      Object.keys(payload).forEach((k) => { if (payload[k] === '') payload[k] = null; });
      // Location is sent as a complete group (blank, not null) so clearing a level really clears it.
      ['country_code', 'country', 'state_code', 'state', 'district_code', 'district', 'city', 'locality']
        .forEach((k) => { if (payload[k] == null) payload[k] = ''; });

      if (isEdit) {
        await leadAPI.update(id, payload);
        toast.success('Lead updated');
        navigate(`/leads/${id}`);
        return;
      }
      const { data: dup } = await leadAPI.findDuplicates({ mobile: form.primary_mobile, email: form.email || undefined });
      if (dup.duplicates?.length && !window.confirm(`Possible duplicate found (${dup.duplicates.map((d) => d.customer_name).join(', ')}). Create this lead anyway?`)) {
        setDuplicateWarning(dup.duplicates);
        setLoading(false);
        return;
      }
      const { data } = await leadAPI.create(payload);
      setCreated(data);
      toast.success(`Lead created \u00b7 ${data.lead_no || ''}`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save lead');
    } finally { setLoading(false); }
  };

  if (blockedEdit) {
    return (
      <div className="max-w-xl space-y-3" data-testid="lead-edit-not-authorized">
        <h1 className="text-2xl font-outfit font-semibold">Editing not allowed</h1>
        <p className="text-sm text-muted-foreground">Your account can create leads but is not authorized to edit an
          existing lead. Ask an administrator for the “Edit Lead” permission.</p>
        <button onClick={() => navigate(`/leads/${id}`)} data-testid="lead-edit-back-btn"
          className="rounded-lg border border-slate-200 px-4 py-2 text-sm dark:border-slate-700">Back to lead</button>
      </div>
    );
  }

  if (created) {
    return (
      <div className="max-w-xl space-y-4" data-testid="lead-created">
        <div className="clay-card space-y-3 p-6">
          <CheckCircle2 className="h-8 w-8 text-green-500" />
          <h1 className="text-2xl font-outfit font-semibold">Lead Created</h1>
          <p className="text-xs text-muted-foreground">Lead ID (server-generated, immutable)</p>
          <p className="font-mono text-2xl font-semibold" data-testid="created-lead-no">{created.lead_no || '\u2014'}</p>
          <p className="text-sm">{created.customer_name} · {created.primary_mobile}</p>
          <div className="flex flex-wrap gap-2 pt-2">
            <button onClick={() => navigate(`/leads/${created.id}`)} data-testid="open-lead-btn" className="rounded-lg bg-primary px-4 py-2 text-sm text-white">Open Lead</button>
            <button onClick={() => { idem.current = `lf-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`; setCreated(null); setForm(emptyForm); }}
              data-testid="create-another-lead-btn" className="rounded-lg border border-slate-200 px-4 py-2 text-sm dark:border-slate-700">Create Another Lead</button>
            <button onClick={() => navigate('/leads')} data-testid="go-to-leads-btn" className="rounded-lg border border-slate-200 px-4 py-2 text-sm dark:border-slate-700">All Leads</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 pb-4" data-testid="lead-form-container">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => navigate('/leads')} data-testid="lead-form-back"
            className="rounded-lg p-2 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800"><ArrowLeft className="h-5 w-5" /></button>
          <div>
            <h1 className="text-2xl font-outfit font-semibold lg:text-3xl">{isEdit ? 'Edit Lead' : 'New Lead'}</h1>
            <p className="text-xs text-muted-foreground">Lead ID is generated by the server on save · Project Serial is issued only when a Project is created</p>
          </div>
        </div>
      </div>

      {duplicateWarning.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="lead-duplicate-warning">
          Possible existing leads: {duplicateWarning.map((d) => d.customer_name).join(', ')}.
        </div>
      )}

      <Section title="Contact Information" hint={contactLocked ? 'Read-only — you do not have Edit Contact Info' : undefined} testId="section-contact">
        <div className="grid grid-cols-1 gap-x-3 gap-y-2.5 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className={lbl}>Customer Type</label>
            <div className="flex gap-2">
              {[['b2c', 'B2C'], ['b2b', 'B2B Partner']].map(([v, l]) => (
                <button key={v} type="button" onClick={() => set('client_type', v)} data-testid={`lead-type-${v}`}
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm ${form.client_type === v ? 'border-primary bg-primary text-white' : 'border-slate-200 dark:border-slate-700'}`}>{l}</button>
              ))}
            </div>
          </div>
          <div>
            <label className={lbl}>Customer / Contact Name{star('customer_name')}</label>
            <input required={isRequired('customer_name')} data-testid="lead-form-name" value={form.customer_name} onChange={(e) => set('customer_name', e.target.value)} className={fld} />
          </div>
          <div>
            <label className={lbl}>Primary Mobile *</label>
            <input type="tel" required disabled={contactLocked} data-testid="lead-form-mobile" value={form.primary_mobile}
              onChange={(e) => set('primary_mobile', e.target.value)} placeholder="9876543210" className={`${fld} disabled:opacity-60`} />
          </div>
          <div>
            <label className={lbl}>Alternative Number</label>
            <input type="tel" disabled={contactLocked} data-testid="lead-form-alt-mobile" value={form.alternative_number}
              onChange={(e) => set('alternative_number', e.target.value)} className={`${fld} disabled:opacity-60`} />
          </div>
          <div>
            <label className={lbl}>WhatsApp Number</label>
            <input type="tel" data-testid="lead-form-whatsapp" value={form.whatsapp_number} disabled={form.whatsapp_same_as_primary || contactLocked}
              onChange={(e) => set('whatsapp_number', e.target.value)} className={`${fld} disabled:opacity-60`} />
            <label className="mt-1 flex items-center gap-2 text-xs">
              <input type="checkbox" data-testid="lead-form-whatsapp-same" checked={form.whatsapp_same_as_primary}
                onChange={(e) => set('whatsapp_same_as_primary', e.target.checked)} />
              Same as Primary Mobile
            </label>
          </div>
          <div>
            <label className={lbl}>Email</label>
            <input type="email" required={isRequired('email')} disabled={contactLocked} data-testid="lead-form-email"
              value={form.email} onChange={(e) => set('email', e.target.value)} className={`${fld} disabled:opacity-60`} />
          </div>
          <div>
            <label className={lbl}>Company Name{star('company_name')}</label>
            <input data-testid="lead-form-company" value={form.company_name} onChange={(e) => set('company_name', e.target.value)} className={fld} />
          </div>
        </div>

        {form.client_type === 'b2b' && (
          <div className="mt-4 rounded-lg border border-purple-200 bg-purple-50/50 p-3 dark:border-purple-900 dark:bg-purple-900/10" data-testid="b2b-block">
            <p className="mb-2 text-xs font-medium text-purple-700 dark:text-purple-300">B2B — the Partner is the business source, the Client is the end customer whose project may later be created.</p>
            <div className="grid grid-cols-1 gap-x-3 gap-y-2.5 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className={lbl}>B2B Partner *</label>
                <select data-testid="lead-form-partner" value={form.partner_id} onChange={(e) => set('partner_id', e.target.value)} className={fld}>
                  <option value="">Select Partner</option>
                  {(partners || []).map((p) => <option key={p.id} value={p.id}>{p.company_name || p.contact_person}{p.contact_person && p.company_name ? ` · ${p.contact_person}` : ''}{p.city ? ` · ${p.city}` : ''}</option>)}
                </select>
                {partners !== null && partners.length === 0 && <p className="mt-1 text-xs text-amber-600" data-testid="partner-list-empty">No B2B Partner records visible (needs the B2B view permission / master data).</p>}
              </div>
              <div>
                <label className={lbl}>Client / End Customer Name *</label>
                <input data-testid="lead-form-client-name" value={form.client_name} onChange={(e) => set('client_name', e.target.value)} className={fld} />
              </div>
              <div>
                <label className={lbl}>Client / End Customer Mobile *</label>
                <input type="tel" disabled={contactLocked} data-testid="lead-form-client-mobile" value={form.client_mobile} onChange={(e) => set('client_mobile', e.target.value)} className={`${fld} disabled:opacity-60`} />
              </div>
              <div>
                <label className={lbl}>Client WhatsApp</label>
                <input type="tel" disabled={contactLocked} data-testid="lead-form-client-whatsapp" value={form.client_whatsapp} onChange={(e) => set('client_whatsapp', e.target.value)} className={`${fld} disabled:opacity-60`} />
              </div>
              <div>
                <label className={lbl}>Client Email</label>
                <input type="email" disabled={contactLocked} data-testid="lead-form-client-email" value={form.client_email} onChange={(e) => set('client_email', e.target.value)} className={`${fld} disabled:opacity-60`} />
              </div>
              <div>
                <label className={lbl}>Client Company Name</label>
                <input data-testid="lead-form-client-company" value={form.client_company_name} onChange={(e) => set('client_company_name', e.target.value)} className={fld} />
              </div>
            </div>
          </div>
        )}
      </Section>

      <Section title="Location" hint="Select from standardized worldwide locations · Locality and Address stay free text" testId="section-location">
        {isEdit && form.city && !form.country_code && (
          <p className="mb-2.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200"
            data-testid="lead-form-legacy-location">
            This lead has an older free-text location (“{[form.city, form.state].filter(Boolean).join(', ')}”). Select a
            Country to standardize it — nothing changes until you save.
          </p>
        )}
        <div className="grid grid-cols-1 gap-x-3 gap-y-2.5 sm:grid-cols-2 lg:grid-cols-3">
          <LocationPicker idPrefix="lead-form" value={form}
            onChange={(patch) => setForm((f) => ({ ...f, ...patch }))} />
          <div>
            <label className={lbl}>Pin Code</label>
            <input data-testid="lead-form-pin" value={form.pin_code} onChange={(e) => set('pin_code', e.target.value)} className={fld} />
          </div>
          <div className="lg:col-span-2">
            <label className={lbl}>Street / Site Address{star('project_address')}</label>
            <input data-testid="lead-form-address" value={form.project_address} onChange={(e) => set('project_address', e.target.value)} className={fld} />
          </div>
          <div>
            <label className={lbl}>Google Maps Location (optional)</label>
            <input type="url" data-testid="lead-form-maps" value={form.google_maps_location} onChange={(e) => set('google_maps_location', e.target.value)} placeholder="https://maps.google.com/..." className={fld} />
          </div>
        </div>
      </Section>

      <Section title="Project & Requirement" hint="Plot Size stays a readable dimension · Plot Area is for irregular plots" testId="section-project">
        <div className="grid grid-cols-1 gap-x-3 gap-y-2.5 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className={lbl}>Project Type{star('project_type')}</label>
            <select data-testid="lead-form-project-type" value={form.project_type} onChange={(e) => set('project_type', e.target.value)} className={fld}>
              <option value="">Select Type</option>
              {PROJECT_TYPES.map((pt) => <option key={pt} value={pt}>{pt[0].toUpperCase() + pt.slice(1)}</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>Plot Size{star('plot_size')}</label>
            <div className="flex gap-2">
              <input data-testid="lead-form-plot-size" value={form.plot_size} onChange={(e) => set('plot_size', e.target.value)} placeholder="30 x 50" className={fld} />
              <select data-testid="lead-form-plot-size-unit" value={form.plot_size_unit} onChange={(e) => set('plot_size_unit', e.target.value)} className={`${fld} w-28`}>
                {PLOT_SIZE_UNITS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={lbl}>Plot Area</label>
            <div className="flex gap-2">
              <input data-testid="lead-form-plot-area" value={form.plot_area} onChange={(e) => set('plot_area', e.target.value)} placeholder="1675" className={fld} />
              <select data-testid="lead-form-plot-area-unit" value={form.plot_area_unit} onChange={(e) => set('plot_area_unit', e.target.value)} className={`${fld} w-28`}>
                {PLOT_AREA_UNITS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={lbl}>Construction Area{star('construction_area')}</label>
            <div className="flex gap-2">
              <input data-testid="lead-form-construction-area" value={form.construction_area} onChange={(e) => set('construction_area', e.target.value)} placeholder="2400" className={fld} />
              <select data-testid="lead-form-construction-unit" value={form.construction_area_unit} onChange={(e) => set('construction_area_unit', e.target.value)} className={`${fld} w-28`}>
                {CONSTRUCTION_UNITS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={lbl}>Plot Position</label>
            <select data-testid="lead-form-plot-position" value={form.plot_position} onChange={(e) => set('plot_position', e.target.value)} className={fld}>
              <option value="">Select Position</option>
              {POSITIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>Plot Facing</label>
            <select data-testid="lead-form-plot-facing" value={form.plot_facing} onChange={(e) => set('plot_facing', e.target.value)} className={fld}>
              <option value="">Select Facing</option>
              {FACINGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>Floors</label>
            <input data-testid="lead-form-floors" value={form.num_floors} onChange={(e) => set('num_floors', e.target.value)} placeholder="G+2 / P+6 / B+G+4" className={fld} />
          </div>
          <div>
            <label className={lbl}>Expected Start Date</label>
            <input type="date" data-testid="lead-form-start-date" value={form.expected_start_date} onChange={(e) => set('expected_start_date', e.target.value)} className={fld} />
          </div>
          <div>
            <label className={lbl}>Site Visit Date</label>
            <input type="date" data-testid="lead-form-site-visit" value={form.site_visit_date} onChange={(e) => set('site_visit_date', e.target.value)} className={fld} />
          </div>
        </div>
        {form.plot_facing === 'custom' && (
          <div className="mt-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
            <CompassPicker angle={form.plot_facing_angle} onChange={(a) => set('plot_facing_angle', a)} />
            <p className="mt-2 text-xs text-muted-foreground">Stored as a bearing; displayed as {facingFromAngle(form.plot_facing_angle)}.</p>
          </div>
        )}
        <div className="mt-3">
          <label className={lbl}>Services Required</label>
          <div className="flex flex-wrap gap-2">
            {services.map((s) => (
              <button key={s.id} type="button" data-testid={`lead-form-service-${s.name}`} onClick={() => toggleService(s.name)}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${form.services_required.includes(s.name)
                  ? 'border-primary bg-primary text-white' : 'border-slate-200 hover:border-primary dark:border-slate-700'}`}>{s.name}</button>
            ))}
          </div>
        </div>
      </Section>

      <Section title="Lead Source, Status & Assignment" hint={sourceLocked ? 'Lead Source is read-only — you do not have Edit Lead Source' : undefined} testId="section-assignment">
        <div className="grid grid-cols-1 gap-x-3 gap-y-2.5 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className={lbl}>Lead Source{star('lead_source')}</label>
            <select required={isRequired('lead_source')} disabled={sourceLocked} data-testid="lead-form-source"
              value={form.lead_source} onChange={(e) => set('lead_source', e.target.value)} className={`${fld} disabled:opacity-60`}>
              {leadSources.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>Campaign Name</label>
            <input disabled={sourceLocked} data-testid="lead-form-campaign" value={form.campaign_name}
              onChange={(e) => set('campaign_name', e.target.value)} className={`${fld} disabled:opacity-60`} />
          </div>
          <div>
            <label className={lbl}>Client Sector{star('client_sector')}</label>
            <SectorSelect sectors={sectors} value={form.client_sector} required={isRequired('client_sector')}
              disabled={sectorLocked} onChange={(v) => set('client_sector', v)} />
          </div>
          <div>
            <label className={lbl}>Follow-up Date{star('follow_up_date')}</label>
            <input type="date" data-testid="lead-form-followup" value={form.follow_up_date} onChange={(e) => set('follow_up_date', e.target.value)} className={fld} />
          </div>
          <div>
            <label className={lbl}>Priority</label>
            <select data-testid="lead-form-priority" value={form.priority} onChange={(e) => set('priority', e.target.value)} className={fld}>
              {PRIORITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              {isEdit && LEGACY_PRIORITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>Lead Status</label>
            <select data-testid="lead-form-status" value={form.status} onChange={(e) => set('status', e.target.value)} className={fld}>
              {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              {isEdit && !STATUSES.some(([v]) => v === form.status) && <option value={form.status}>{form.status}</option>}
            </select>
          </div>
          <div>
            <label className={lbl}>Assigned Sales Executive</label>
            <select data-testid="lead-form-assigned" value={form.assigned_to} onChange={(e) => set('assigned_to', e.target.value)} className={fld} disabled={!canAssign}>
              <option value="">{canAssign ? 'Unassigned' : 'Assigned automatically'}</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({(u.role || '').replace('_', ' ')})</option>)}
            </select>
          </div>
        </div>

        {REASON_REQUIRED.includes(form.status) && (
          <div className="mt-3" data-testid="status-reason-block">
            <label className={lbl}>Reason * (mandatory for {STATUSES.find((s) => s[0] === form.status)[1]})</label>
            <input data-testid="lead-form-status-reason" value={form.status_reason} onChange={(e) => set('status_reason', e.target.value)}
              placeholder="Why is this lead not moving forward?" className={fld} />
          </div>
        )}

        {canAssign && intel?.executives?.length > 0 && (
          <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700" data-testid="exec-workload">
            <table className="w-full min-w-[640px] text-xs">
              <thead className="text-left text-muted-foreground">
                <tr><th className="px-2 py-1.5">Sales Executive</th><th>Today</th><th>Active</th><th>Follow-ups Due</th><th>Overdue</th><th>Won</th><th>Conversion</th><th>Workload</th><th /></tr>
              </thead>
              <tbody>
                {intel.executives.map((e) => (
                  <tr key={e.user_id} className={`border-t border-slate-100 dark:border-slate-800 ${form.assigned_to === e.user_id ? 'bg-amber-50 dark:bg-amber-900/20' : ''}`}>
                    <td className="px-2 py-1.5">{e.name}{intel.recommended_user_id === e.user_id && <span className="ml-1 text-primary">★</span>}</td>
                    <td data-testid={`exec-today-${e.user_id}`}>{e.leads_today}</td>
                    <td data-testid={`exec-active-${e.user_id}`}>{e.active_leads}</td>
                    <td>{e.pending_follow_ups}</td>
                    <td className={e.overdue_follow_ups > 0 ? 'text-red-600' : ''}>{e.overdue_follow_ups}</td>
                    <td>{e.won_leads}</td>
                    <td>{e.conversion_rate}%</td>
                    <td data-testid={`exec-workload-${e.user_id}`}>{e.current_workload}</td>
                    <td><button type="button" data-testid={`exec-pick-${e.user_id}`} onClick={() => set('assigned_to', e.user_id)}
                      className="rounded px-2 py-0.5 text-primary underline">Assign</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {intel.recommended_reason && <p className="px-2 py-1.5 text-xs text-muted-foreground" data-testid="exec-recommendation">{intel.recommended_reason}</p>}
          </div>
        )}
        {selectedExec && (
          <p className="mt-2 text-xs text-muted-foreground" data-testid="selected-exec-summary">
            <Flame className="mr-1 inline h-3 w-3 text-amber-500" />
            {selectedExec.name}: {selectedExec.active_leads} active · {selectedExec.pending_follow_ups} follow-ups due · {selectedExec.conversion_rate}% conversion
          </p>
        )}
      </Section>

      <Section title="Qualification & Notes" hint="Project Serial, contract value and payments belong to Project creation" testId="section-qualification">
        <div className="grid grid-cols-1 gap-x-3 gap-y-2.5 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className={lbl}>Estimated Budget (₹){star('budget')}</label>
            <input type="number" data-testid="lead-form-budget" value={form.budget} onChange={(e) => set('budget', e.target.value)} placeholder="1000000" className={fld} />
          </div>
          <div>
            <label className={lbl}>Win Probability (%)</label>
            <input type="number" min="0" max="100" data-testid="lead-form-probability" value={form.probability} onChange={(e) => set('probability', e.target.value)} className={fld} />
          </div>
          <div>
            <label className={lbl}>Tags</label>
            <input data-testid="lead-form-tags" value={(form.tags || []).join(', ')} placeholder="Investor, Builder"
              onChange={(e) => set('tags', e.target.value.split(',').map((t) => t.trim()).filter(Boolean))} className={fld} />
          </div>
        </div>
        {isEdit && (
          <div className="mt-2.5 grid grid-cols-1 gap-x-3 gap-y-2.5 sm:grid-cols-2 lg:grid-cols-3" data-testid="legacy-closure-fields">
            <div>
              <label className={lbl}>Final Deal Amount (₹)</label>
              <input type="number" data-testid="lead-form-final-deal" value={form.final_deal_amount} onChange={(e) => set('final_deal_amount', e.target.value)} className={fld} />
            </div>
            <div>
              <label className={lbl}>Advance Received (₹)</label>
              <input type="number" data-testid="lead-form-advance" value={form.advance_received} onChange={(e) => set('advance_received', e.target.value)} className={fld} />
            </div>
            <div>
              <label className={lbl}>Agreement Date</label>
              <input type="date" value={form.agreement_date} onChange={(e) => set('agreement_date', e.target.value)} className={fld} />
            </div>
            <label className="flex items-center gap-2 text-sm sm:col-span-2 lg:col-span-3">
              <input type="checkbox" data-testid="lead-form-agreement-signed" checked={form.agreement_signed} onChange={(e) => set('agreement_signed', e.target.checked)} />
              Agreement Signed
            </label>
            <p className="text-xs text-muted-foreground sm:col-span-2 lg:col-span-3">
              Existing closure fields are kept for legacy projects only. They never create a Project or assign an Architect — use Create Project for that.
            </p>
          </div>
        )}
        <div className="mt-3">
          <label className={lbl}>Remarks{star('remarks')}</label>
          <textarea data-testid="lead-form-remarks" rows={2} value={form.remarks} onChange={(e) => set('remarks', e.target.value)} className={fld} />
        </div>
      </Section>

      {/* Sticky action bar — right aligned, Cancel then Create, reachable at any scroll position */}
      <div className="sticky bottom-0 z-30 -mx-1 flex justify-end gap-2 border-t border-slate-200 bg-white/95 px-1 py-2.5 backdrop-blur dark:border-slate-700 dark:bg-slate-900/95"
        data-testid="lead-form-actions">
        <button type="button" onClick={() => navigate(isEdit ? `/leads/${id}` : '/leads')} data-testid="lead-form-cancel"
          className="rounded-lg border border-slate-200 px-5 py-2.5 text-sm dark:border-slate-700">Cancel</button>
        <button type="submit" disabled={loading} data-testid="lead-form-submit"
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-white disabled:opacity-50">
          <Save className="h-4 w-4" />{loading ? 'Saving\u2026' : isEdit ? 'Update Lead' : 'Create Lead'}
        </button>
      </div>
    </form>
  );
};

export default LeadFormPage;
